import styled from '@emotion/styled';
import React, { Suspense } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { Redirect } from 'react-router-dom';

import type { FreeTextbooksSubtopicViewQuery } from '__free_textbooks_generated__/FreeTextbooksSubtopicViewQuery.graphql';
import { ErrorBoundary } from 'ms-components/ErrorBoundary/ErrorBoundary';
import { FreeCoreTextbookSubtopicDetailView } from 'ms-pages/FreeTextbooks/FreeTextbooksSubtopicView/FreeCoreTextbookSubtopicDetailView';
import { FreeLegacyTextbookSubtopicDetailView } from 'ms-pages/FreeTextbooks/FreeTextbooksSubtopicView/FreeLegacyTextbookSubtopicDetailView';
import { ResponsiveContainer } from 'ms-pages/Teacher/components/Container';
import MinorSpinner from 'ms-pages/Teacher/components/MinorSpinner';
import { breakPoints } from 'ms-styles/base';
import { colors } from 'ms-styles/colors';
import Separator from 'ms-ui-primitives/Separator';
import { minBreak } from 'ms-utils/emotion';
import { loginUrl } from 'ms-utils/urls';

import { FreeTextbooksSubtopicViewFooter } from '../FreeTextbooksSubtopicViewFooter/FreeTextbooksSubtopicViewFooter';
import { FreeTextbooksSubtopicViewHeader } from '../FreeTextbooksSubtopicViewHeader/FreeTextbooksSubtopicViewHeader';

// TODO have a unique definition for these ones
const PADDING = 32;
const PADDING_SMALL_SCREENS = 16;

const Wrapper = styled.div({
  width: '100%',
  height: '100%',
  overflow: 'auto',
});

const ContentWrapper = styled.div({
  maxWidth: breakPoints.medium,
  margin: 'auto',
  paddingLeft: PADDING_SMALL_SCREENS,
  paddingRight: PADDING_SMALL_SCREENS,

  ...minBreak(breakPoints.small, {
    paddingLeft: PADDING,
    paddingRight: PADDING,
  }),
});

const FooterBackground = styled.div({
  marginTop: 24,
  borderTopWidth: 2,
  borderColor: colors.ironLight,
  borderStyle: 'solid',
});

type Props = { subtopicId: string; syllabusId: string };

export function FreeTextbooksSubtopicView({ subtopicId, syllabusId }: Props) {
  return (
    <ErrorBoundary
      name="FreeTextbooksSubtopicView"
      fallback="Error loading subtopic"
    >
      <Suspense fallback={<MinorSpinner />}>
        <FreeTextbooksSubtopicViewInner
          subtopicId={subtopicId}
          syllabusId={syllabusId}
        />
      </Suspense>
    </ErrorBoundary>
  );
}

function FreeTextbooksSubtopicViewInner({ subtopicId, syllabusId }: Props) {
  const data = useLazyLoadQuery<FreeTextbooksSubtopicViewQuery>(
    graphql`
      query FreeTextbooksSubtopicViewQuery($syllabusId: ID!) {
        syllabus(id: $syllabusId) {
          textbookType
        }
      }
    `,
    React.useMemo(() => ({ syllabusId }), [syllabusId]),
    {
      networkCacheConfig: {
        // @ts-expect-error TODO fix this once we upgrade relay and have sound types
        useCache: true,
        ttl: Infinity,
      },
    },
  );

  // I wish I could use a regular React ref with useRef but
  // I cannot make it work reliably for this purpose.
  // Using plain old ref callback and store the value in state works great instead
  const [scrollingElement, _setScrollingElement] =
    React.useState<HTMLDivElement | null>(null);
  const refCallback = React.useCallback<(element: HTMLDivElement) => void>(
    element => {
      _setScrollingElement(element);
    },
    [],
  );

  if (data.syllabus === null) {
    if (window.inSSR !== true) {
      window.location.assign(loginUrl);
      return null;
    }
    return <Redirect to={loginUrl} />;
  }
  return (
    <Wrapper ref={refCallback}>
      <FreeTextbooksSubtopicViewHeader subtopicId={subtopicId} />
      <ResponsiveContainer narrow>
        <ContentWrapper>
          {data.syllabus.textbookType === 'CORE_TEXTBOOK' ? (
            <FreeCoreTextbookSubtopicDetailView subtopicId={subtopicId} />
          ) : (
            <FreeLegacyTextbookSubtopicDetailView
              subtopicId={subtopicId}
              scrollingElement={scrollingElement}
            />
          )}
        </ContentWrapper>
      </ResponsiveContainer>

      <FooterBackground>
        <ResponsiveContainer narrow>
          <ContentWrapper>
            <Separator size={8} />
            <FreeTextbooksSubtopicViewFooter subtopicId={subtopicId} />
          </ContentWrapper>
        </ResponsiveContainer>
      </FooterBackground>
    </Wrapper>
  );
}
